import { RefObject, useEffect, useState } from "react"
import { LexicalEditor } from "lexical"

interface EditorRef {
  getEditor: () => LexicalEditor | null
}

export function useEditorFocused(editorRef: RefObject<EditorRef | null>): boolean {
  const [focused, setFocused] = useState(false)

  useEffect(() => {
    let cleanup: (() => void) | undefined

    const tryAttach = () => {
      const root = editorRef.current?.getEditor()?.getRootElement()
      if (!root) return false

      const onFocusIn = () => setFocused(true)
      const onFocusOut = (e: FocusEvent) => {
        if (e.relatedTarget instanceof Node && root.contains(e.relatedTarget)) return
        setFocused(false)
      }

      root.addEventListener("focusin", onFocusIn)
      root.addEventListener("focusout", onFocusOut)

      cleanup = () => {
        root.removeEventListener("focusin", onFocusIn)
        root.removeEventListener("focusout", onFocusOut)
      }
      return true
    }

    if (tryAttach()) return () => cleanup?.()

    const id = setInterval(() => {
      if (tryAttach()) clearInterval(id)
    }, 100)

    return () => {
      clearInterval(id)
      cleanup?.()
    }
  }, [editorRef])

  return focused
}
